import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldAlert,
  CheckCircle2,
  Circle,
  ExternalLink,
  Facebook,
  Instagram,
  AlertTriangle,
  Loader2,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GradientMesh from "@/components/GradientMesh";
import GlassCard from "@/components/GlassCard";

const SECTION_ORDER = [
  "app_identity",
  "domains_and_urls",
  "oauth_redirects",
  "permissions_to_request",
  "business_verification",
  "test_users_and_assets",
  "review_notes_template",
  "postmoro_screens_reviewers_will_test",
  "live_flags",
];

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard");
  } catch (_e) {
    toast.error("Couldn't copy — please select and copy manually.");
  }
}

function FlagBadge({ on }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " +
        (on
          ? "bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)]"
          : "bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)]")
      }
      data-testid={`meta-flag-${on ? "on" : "off"}`}
    >
      {on ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {on ? "ON" : "OFF"}
    </span>
  );
}

function RuntimeFlagRow({ label, value, hint, on }) {
  const isUrl = typeof value === "string" && /^https?:\/\//.test(value);
  return (
    <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[color:var(--text-1)]">{label}</div>
          {hint ? (
            <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 leading-relaxed">{hint}</div>
          ) : null}
        </div>
        <div className="shrink-0">
          {on === undefined ? (
            !isUrl ? (
              <div className="text-[11px] text-[color:var(--text-2)] font-mono">{value || "—"}</div>
            ) : null
          ) : (
            <FlagBadge on={on} />
          )}
        </div>
      </div>
      {on === undefined && isUrl ? (
        <div className="mt-2 text-[11px] text-[color:var(--text-2)] font-mono break-all leading-relaxed">
          {value}
        </div>
      ) : null}
    </div>
  );
}

function ChecklistItem({ item }) {
  const value = item.value || "";
  const isCopyable = typeof value === "string" && value.length > 0 && value.length < 240 && !value.includes("\n");
  return (
    <div
      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
      data-testid={`meta-checklist-item-${item.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[color:var(--text-1)]">{item.label}</div>
          {value ? (
            <div className="text-[12px] text-[color:var(--text-2)] mt-1 break-all leading-relaxed font-mono whitespace-pre-wrap">
              {value}
            </div>
          ) : (
            <div className="text-[11px] text-[color:var(--text-3)] italic mt-1">
              Set this value in the Meta App Dashboard.
            </div>
          )}
          {item.note ? (
            <div className="text-[11px] text-[color:var(--text-3)] mt-2 leading-relaxed">{item.note}</div>
          ) : null}
        </div>
        {isCopyable ? (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0 h-7 w-7 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
            onClick={() => copyToClipboard(value)}
            data-testid={`meta-checklist-copy-${item.key}`}
            aria-label={`Copy ${item.label}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ChecklistSection({ sectionKey, section }) {
  return (
    <GlassCard className="p-5" data-testid={`meta-section-${sectionKey}`}>
      <div className="font-display text-lg font-semibold text-[color:var(--text-1)] mb-3">
        {section.title}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {section.items.map((item) => (
          <ChecklistItem key={item.key} item={item} />
        ))}
      </div>
    </GlassCard>
  );
}

export default function AdminMetaChecklistPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/admin/meta/checklist");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Meta App Review Checklist"
          subtitle="Everything you need to ship live Facebook Page + Instagram Business publishing. Owner-only — nothing here is exposed to customers."
          testidPrefix="admin-meta-checklist"
        />

        <div className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.04)] p-3 mb-5 text-xs text-[color:var(--accent-warm)] flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <strong className="text-[color:var(--text-1)]">Do not submit to Meta yet.</strong>{" "}
            This is a preparation checklist. Use it to gather every URL, screenshot, screencast, and
            test account Meta will ask for, BEFORE you click "Submit for Review" in the Meta App Dashboard.
          </div>
        </div>

        {loading ? (
          <GlassCard className="p-10 flex items-center justify-center text-[color:var(--text-3)]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading checklist…
          </GlassCard>
        ) : err ? (
          <GlassCard className="p-6 text-sm text-[color:var(--accent-warm)] flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-[color:var(--text-1)]">Couldn't load checklist</div>
              <div className="mt-1">{err}</div>
            </div>
          </GlassCard>
        ) : !data ? null : (
          <div className="space-y-5">
            {/* Runtime status — what's currently ON/OFF on the server */}
            <GlassCard className="p-5" data-testid="meta-runtime-status">
              <div className="flex items-center gap-2 mb-3">
                <div className="font-display text-lg font-semibold text-[color:var(--text-1)]">
                  Current server state
                </div>
                <div className="text-[11px] text-[color:var(--text-3)]">
                  (live values from /api right now)
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <RuntimeFlagRow
                  label="Meta credentials configured"
                  hint="META_APP_ID + META_APP_SECRET + META_REDIRECT_URI present in backend .env"
                  on={data.runtime_flags?.meta_configured}
                />
                <RuntimeFlagRow
                  label="META_FACEBOOK_LIVE_ENABLED"
                  hint="When ON, normal customers can connect Facebook and publish live. Flip after Meta approves Facebook scopes."
                  on={data.runtime_flags?.facebook_live_enabled}
                />
                <RuntimeFlagRow
                  label="META_INSTAGRAM_LIVE_ENABLED"
                  hint="When ON, normal customers can connect Instagram and publish live. Flip after Meta approves Instagram scopes."
                  on={data.runtime_flags?.instagram_live_enabled}
                />
                <RuntimeFlagRow
                  label="META_PAGE_PUBLISHING_ENABLED"
                  hint="Customer-facing 'Enable Page publishing' CTA. Secondary gate; flip after the live flag above."
                  on={data.runtime_flags?.fb_page_publishing_enabled}
                />
                <RuntimeFlagRow
                  label="Current redirect URI"
                  value={data.runtime_flags?.meta_redirect_uri}
                  hint="This must exactly match the OAuth redirect URI configured in the Meta App Dashboard. Don't change it until postmoro.com is live."
                />
                <RuntimeFlagRow
                  label="App ID / Secret"
                  value={
                    (data.runtime_flags?.meta_app_id_present ? "App ID ✓" : "App ID ✗") +
                    " / " +
                    (data.runtime_flags?.meta_app_secret_present ? "Secret ✓" : "Secret ✗")
                  }
                  hint="Credentials live in backend .env. Do not change in production until cutover."
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brand-primary)] hover:underline"
                  data-testid="meta-app-dashboard-link"
                >
                  <ExternalLink className="h-3 w-3" /> Open Meta App Dashboard
                </a>
                <a
                  href="https://developers.facebook.com/docs/app-review/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brand-primary)] hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Meta App Review docs
                </a>
                <a
                  href="https://business.facebook.com/settings/security"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brand-primary)] hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Business Verification
                </a>
              </div>
            </GlassCard>

            {/* Platform readiness summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GlassCard className="p-5" data-testid="meta-fb-readiness">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:rgba(24,119,242,0.15)] text-[color:#1877F2]">
                    <Facebook className="h-4 w-4" />
                  </div>
                  <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                    Facebook Page readiness
                  </div>
                  <FlagBadge on={data.runtime_flags?.facebook_live_enabled} />
                </div>
                <ul className="space-y-1.5">
                  {(data.facebook_requirements || []).map((req, i) => (
                    <li
                      key={i}
                      className="text-xs text-[color:var(--text-2)] flex items-start gap-2 leading-relaxed"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--accent-warm)] mt-1.5 shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </GlassCard>

              <GlassCard className="p-5" data-testid="meta-ig-readiness">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:rgba(228,64,95,0.15)] text-[color:#E4405F]">
                    <Instagram className="h-4 w-4" />
                  </div>
                  <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                    Instagram Business readiness
                  </div>
                  <FlagBadge on={data.runtime_flags?.instagram_live_enabled} />
                </div>
                <ul className="space-y-1.5">
                  {(data.instagram_requirements || []).map((req, i) => (
                    <li
                      key={i}
                      className="text-xs text-[color:var(--text-2)] flex items-start gap-2 leading-relaxed"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--accent-warm)] mt-1.5 shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            </div>

            {/* Structured checklist sections */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {SECTION_ORDER.map((key) => {
                const section = data.checklist?.[key];
                if (!section) return null;
                return <ChecklistSection key={key} sectionKey={key} section={section} />;
              })}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
